import React, { createContext, useMemo } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { REGIONS_ENDPOINT } from '@/constants'
import type { Region } from '@/types/app-types'

import {
    allProductionsBlockTitleSelector,
    articleSelector,
    breadcrumbsSelector,
    categoryBlockTitleMapSelector,
    categoryIdSelector,
    categoryOverridesSelector,
    categoryWebPathMapSelector,
    listTitleSelector,
    metadataSelector,
    pageSubtitleSelector,
    pageTitleSelector,
    regionSelector,
    responsiveImageUrlsSelector,
    topPicksBlockTitleSelector,
} from './selectors'
import type { RegionContextType, RegionParams, RegionProviderProps, RegionSelectors, RegionState } from './types'

const RegionContext = createContext<RegionContextType>(undefined as any)
const { Provider } = RegionContext

const getRegionEndpoint = (regionId: number): string => {
    return `${REGIONS_ENDPOINT}/${regionId}`
}

export const RegionProvider: React.FC<RegionProviderProps> = ({
    children,
    initialParams,
    initialProps,
    categoryId,
}) => {
    const { regionId, ...queryParams } = initialParams
    const regionEndpoint: string = getRegionEndpoint(regionId)

    const { fetching, data, error } = useQuery<Region>({
        endpoint: regionEndpoint,
        params: queryParams,
        initialFetchDisabled: !!initialProps?.data,
        initialData: initialProps?.data,
        enabled: !!regionId,
    })

    const contextValue: RegionContextType = useMemo(() => {
        const state: RegionState = { fetching, data, error, categoryId }
        const selectors: RegionSelectors = {
            region: regionSelector(state),
            categoryId: categoryIdSelector(state),
            categoryWebPathMap: categoryWebPathMapSelector(state),
            categoryOverrides: categoryOverridesSelector(state),
            responsiveImageUrls: responsiveImageUrlsSelector(state),
            listTitle: listTitleSelector(state),
            pageTitle: pageTitleSelector(state),
            pageSubtitle: pageSubtitleSelector(state),
            topPicksBlockTitle: topPicksBlockTitleSelector(state),
            categoryBlockTitleMap: categoryBlockTitleMapSelector(state),
            allProductionsBlockTitle: allProductionsBlockTitleSelector(state),
            breadcrumbs: breadcrumbsSelector(state, false),
            metadata: metadataSelector(state),
            article: articleSelector(state),
        }
        return { state, selectors }
    }, [fetching, data, error, categoryId])

    return <Provider value={contextValue}>{children}</Provider>
}

export const serverSideRead = async (params: RegionParams, maxAgeMs?: number): Promise<Region> => {
    const { regionId, ...queryParams } = params
    const regionEndpoint: string = getRegionEndpoint(regionId)
    const queryInstance = serverSideQuery<Region>(regionEndpoint)
    const region: Region = await queryInstance(queryParams, maxAgeMs)
    return region
}

export default RegionContext
